"use client";

import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import Link from "next/link";
import type { MouseEventHandler } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  LuChevronLeft,
  LuChevronRight,
  LuLayers,
  LuLink2,
} from "react-icons/lu";

import { AddToCollectionDropdown } from "@/components/shared/add-to-collection-dropdown";
import { StopPropagationWrapper } from "@/components/shared/stop-propagation-wrapper";
import { Button, buttonVariants } from "@/components/ui/button";
import { dialogCloseClassName } from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePostCacheContext } from "@/contexts/post-cache-context";
import { cn } from "@/utils/lib";
import {
  getSafeMediaDimensions,
  hasValidMediaDimensions,
  normalizeMediaDimension,
} from "@/utils/media-dimensions";
import type { ImageMetadata } from "@/utils/media-utils";
import {
  getMediaClientIdFromUrl,
  getMediaMetaFromUrl,
} from "@/utils/post-utils";
import type { PostMediaMeta } from "@/utils/types";

interface FeedImageCarouselProps {
  urls: string[];
  postHash?: string;
  initialSelectedIndex?: number;
  maxHeight?: number;
  maxWidth?: number;
  onSelect?: (index: number) => void;
  singleImageClassName?: string;
  imageContainerClassName?: string;
  optimize?: boolean;
  mediaMeta?: PostMediaMeta;
  keyboardNavigation?: boolean;
  sourceLink?: string;
  fullscreen?: boolean;
}

// We need to pass the render width of the image to the Image component
// This util will calculate width/height while keeping aspect ratio
const getScaledImageSize = (
  width: number,
  height: number,
  renderedWidth: number,
) => {
  const dimensions = getSafeMediaDimensions({ width, height });
  const safeRenderedWidth = normalizeMediaDimension(
    renderedWidth,
    dimensions.width,
  );

  return {
    width: safeRenderedWidth,
    height: Math.max(
      Math.round((safeRenderedWidth / dimensions.width) * dimensions.height),
      1,
    ),
  };
};

const Slide = ({
  url,
  optimize,
  maxHeight,
  mediaMeta = {},
  maxWidth,
  singleImageClassName,
  fullscreen,
}: {
  url: string;
  optimize?: boolean;
  maxHeight: number;
  mediaMeta?: PostMediaMeta;
  maxWidth: number;
  singleImageClassName?: string;
  postHash?: string;
  sourceLink?: string;
  fullscreen?: boolean;
}) => {
  const { getPostImageSize, postsImageSizeMap } = usePostCacheContext();
  // DESO-2954: These components are also used in the message page which doesn't have a post context.  We should refactor
  // these components to not rely on the post context. For now the fallback to an empty object is a quick fix to prevent the app from crashing.
  const metaData = getMediaMetaFromUrl(url, mediaMeta) as ImageMetadata;
  const hasUsableMetaData = hasValidMediaDimensions(metaData);

  // Legacy for items without metadata
  const {
    width: cachedWidth,
    height: cachedHeight,
    isFallback,
  } = getPostImageSize(url);

  const fallbackDimensions = getSafeMediaDimensions({
    width: cachedWidth,
    height: cachedHeight,
  });
  const imageMeta = getSafeMediaDimensions(
    hasUsableMetaData ? metaData : null,
    fallbackDimensions,
  );
  const { width, height } = getScaledImageSize(
    imageMeta.width,
    imageMeta.height,
    Math.min(
      imageMeta.width,
      normalizeMediaDimension(maxWidth, imageMeta.width),
    ),
  );

  const imageRef = useRef<HTMLImageElement>(null!);

  const Component = optimize ? Image : "img";

  useEffect(() => {
    // We have the image's metadata, no need to calculate
    // We just leave this here as a fallback in case it's missing somehow
    if (hasUsableMetaData || !isFallback) return;

    const { naturalWidth, naturalHeight } = imageRef.current;

    if (naturalWidth && naturalHeight) {
      postsImageSizeMap.set(url, {
        width: naturalWidth,
        height: naturalHeight,
      });
    } else {
      imageRef.current.addEventListener(
        "load",
        (ev) => {
          const target = ev.currentTarget as HTMLImageElement;
          const { naturalWidth, naturalHeight } = target;
          if (naturalWidth && naturalHeight) {
            postsImageSizeMap.set(url, {
              width: naturalWidth,
              height: naturalHeight,
            });
            const { width, height } = getScaledImageSize(
              naturalWidth,
              naturalHeight,
              Math.min(naturalWidth, maxWidth),
            );
            target.setAttribute("width", width.toString());
            target.setAttribute(
              "height",
              Math.min(height, maxHeight).toString(),
            );
            target.style.aspectRatio = `${naturalWidth} / ${naturalHeight}`;
          }
        },
        { once: true },
      );
    }
  }, []);

  return (
    <div
      className={cn(
        "embla__slide relative rounded-xl",
        fullscreen && "pointer-events-none",
      )}
    >
      <Component
        ref={imageRef}
        src={url ?? ""}
        style={{
          aspectRatio: `${width} / ${height}`,
        }}
        className={cn(
          `
            mx-auto rounded-none object-contain object-center
          `,
          fullscreen
            ? "pointer-events-auto size-auto! max-h-dvh max-w-dvw"
            : "max-h-screen max-w-full",
          singleImageClassName,
        )}
        {...{
          width,
          height: Math.min(height, maxHeight),
        }}
        quality={optimize ? 90 : undefined}
        alt="Image"
        priority={optimize ? true : undefined}
        unoptimized={optimize ? url?.endsWith(".gif") : undefined}
        decoding="async"
      />
    </div>
  );
};

export const PostImageCarousel = ({
  urls,
  // Images have max-h-screen applied to them, so no worries
  maxHeight = 100000,
  maxWidth = 100000,
  initialSelectedIndex = 0,
  singleImageClassName,
  imageContainerClassName,
  onSelect = () => {},
  optimize = true,
  mediaMeta = {},
  keyboardNavigation = false,
  postHash,
  sourceLink,
  fullscreen,
}: FeedImageCarouselProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    startIndex: initialSelectedIndex,
  });
  const [selectedIndex, setSelectedIndex] = useState(initialSelectedIndex);
  const [isCollectionMenuOpen, setIsCollectionMenuOpen] = useState(false);
  const scrollSnaps = urls.map((_, index) => index);

  const scrollNext: MouseEventHandler<HTMLButtonElement> = useCallback(
    (ev) => {
      ev.stopPropagation();
      if (emblaApi) emblaApi.scrollNext();
    },
    [emblaApi],
  );

  const scrollPrev: MouseEventHandler<HTMLButtonElement> = useCallback(
    (ev) => {
      ev.stopPropagation();

      if (emblaApi) emblaApi.scrollPrev();
    },
    [emblaApi],
  );

  useEffect(() => {
    if (!emblaApi) return;

    const handleSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on("select", handleSelect);

    // Return a cleanup function that explicitly returns void
    return () => {
      if (emblaApi) {
        emblaApi.off("select", handleSelect);
      }
    };
  }, [emblaApi]);

  useEffect(() => {
    onSelect(selectedIndex);
  }, [selectedIndex, onSelect]);

  useEffect(() => {
    if (!emblaApi || !keyboardNavigation) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        emblaApi.scrollNext();
      } else if (e.key === "ArrowLeft") {
        emblaApi.scrollPrev();
      }
    };

    document.body.addEventListener("keydown", handleKeyDown);

    return () => {
      if (!keyboardNavigation) return;
      document.body.removeEventListener("keydown", handleKeyDown);
    };
  }, [emblaApi, keyboardNavigation, scrollNext, scrollPrev]);

  const collectionDropdownChildren = !fullscreen ? (
    <LuLayers
      className={cn(
        `
          relative z-1 cursor-pointer p-0 text-xl text-muted-foreground
          hover:text-primary
        `,
        {
          "text-primary": isCollectionMenuOpen,
        },
      )}
    />
  ) : (
    <Button
      variant="outline"
      className={cn(dialogCloseClassName, "static", {
        "bg-card text-muted-foreground": isCollectionMenuOpen,
      })}
    >
      <LuLayers className="size-4" />
    </Button>
  );

  return (
    <>
      {(postHash || sourceLink) && (
        <StopPropagationWrapper
          className={cn(
            `
              absolute z-10 flex items-center gap-4 rounded-lg bg-background/80
              transition
              md:bg-transparent
            `,
            {
              "pointer-events-auto opacity-100": fullscreen,
              "pointer-events-auto opacity-100 md:bg-background/80":
                !fullscreen && isCollectionMenuOpen,
              "pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:bg-background/80 group-hover:opacity-100":
                !fullscreen && !isCollectionMenuOpen,
              "top-16 right-5 flex-col-reverse gap-2": fullscreen,
              "right-1 bottom-1 gap-4 p-2": !fullscreen,
            },
          )}
        >
          {sourceLink && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    href={sourceLink}
                    className={cn(
                      fullscreen &&
                        buttonVariants({
                          variant: "outline",
                        }),
                      fullscreen && dialogCloseClassName,
                      "static",
                      {
                        "text-muted-foreground hover:text-primary": !fullscreen,
                      },
                    )}
                  >
                    <LuLink2
                      className={cn({
                        "relative z-1 p-0 text-xl": !fullscreen,
                        "size-4": fullscreen,
                      })}
                    />
                  </Link>
                </TooltipTrigger>
                <TooltipContent>Go to Source Post</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {postHash && (
            <AddToCollectionDropdown
              types={["gallery"]}
              createText="New Gallery"
              addToText="Add to Gallery"
              itemData={{
                type: "image",
                postHash,
                clientId: getMediaClientIdFromUrl(urls[selectedIndex]),
              }}
              align="end"
              onOpenChange={setIsCollectionMenuOpen}
            >
              {collectionDropdownChildren}
            </AddToCollectionDropdown>
          )}
        </StopPropagationWrapper>
      )}
      <div
        className={cn(
          "relative self-center",
          fullscreen
            ? "pointer-events-none w-screen max-w-dvw min-w-0"
            : "w-full",
        )}
      >
        <div
          className={cn(
            `embla z-10 rounded-xl border border-border bg-black`,
            imageContainerClassName,
          )}
          ref={emblaRef}
        >
          <div className="embla__container">
            {urls.map((url) => (
              <Slide
                key={url}
                mediaMeta={mediaMeta}
                url={url}
                maxWidth={maxWidth}
                maxHeight={maxHeight}
                optimize={optimize}
                singleImageClassName={singleImageClassName}
                postHash={postHash}
                sourceLink={sourceLink}
                fullscreen={fullscreen}
              />
            ))}
          </div>
        </div>
        <div className="absolute top-[45%] w-full">
          {selectedIndex > 0 && (
            <Button
              onClick={scrollPrev}
              className="pointer-events-auto absolute left-5 z-10 bg-card text-foreground"
              variant="secondary"
              size="icon"
            >
              <LuChevronLeft className="text-lg" />
            </Button>
          )}
          {selectedIndex < urls.length - 1 && (
            <Button
              onClick={scrollNext}
              className="pointer-events-auto absolute right-5 z-10 bg-card text-foreground"
              variant="secondary"
              size="icon"
            >
              <LuChevronRight className="text-lg" />
            </Button>
          )}
        </div>
        {urls.length > 1 && (
          <div
            className="
              pointer-events-none absolute inset-x-0 bottom-0 flex
              justify-center p-4
            "
          >
            {scrollSnaps.map((snap) => (
              <button
                key={snap}
                className={`
                  pointer-events-auto mx-1 size-2 rounded-full border-2
                  border-none border-border ring-2 ring-black
                  ${snap === selectedIndex ? "bg-white" : "bg-white/20"}
                `}
                onClick={(ev) => {
                  ev.stopPropagation();
                  emblaApi?.scrollTo(snap);
                }}
              >
                {snap === selectedIndex && (
                  <div className="mx-0.5 size-1 bg-black"></div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
};
